// IM-EXT-010 IM-UI-045..050 — SWOT interativa e governada pelo usuário.
// Quatro quadrantes de mesma altura (cabeçalho fixo, área interna rolável só quando precisa, contador e estado vazio
// iguais); em tela estreita, uma coluna sem rolagem interna, com prévia e "Ver todos". Cada item mostra a origem
// (gerado pela análise, importado do contexto, adicionado/editado/reclassificado pelo usuário) e oferece, num menu
// compacto: editar, mover, arquivar (com desfazer), ver original e histórico. Execução histórica e perfil sem
// permissão são somente leitura. A natureza, a confiança e as fontes continuam as do achado original.
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '../../api/client';
import { useToast } from '../../components/Toast';
import { Modal } from '../../components/Modal';
import {
  fmtDate, marketApi, QUADRANT_LABEL, SWOT_QUADRANTS, type SwotData, type SwotFields, type SwotHistory, type SwotItem, type SwotQuadrant, type SwotReviewDecision,
} from '../../data/market';
import { Legend, Nature, Sources } from './shared';

const PREVIEW = 3;
const PRIORITY_OPTS: Array<[string, string]> = [['', 'Sem prioridade'], ['1', 'Alta'], ['2', 'Média'], ['3', 'Baixa']];
const IMPACT_OPTS: Array<[string, string]> = [['', 'Não informado'], ['ALTA', 'Alto'], ['MEDIA', 'Médio'], ['BAIXA', 'Baixo']];
const plural = (n: number) => `${n} ${n === 1 ? 'item' : 'itens'}`;

/** Tela estreita (uma coluna, sem rolagem interna). */
function useNarrow(query = '(max-width: 860px)'): boolean {
  const media = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query) : null);
  const [narrow, setNarrow] = useState(() => !!media()?.matches);
  useEffect(() => {
    const mq = media();
    if (!mq) return;
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, [query]);
  return narrow;
}

type Dialog =
  | { kind: 'create'; quadrant: SwotQuadrant }
  | { kind: 'edit'; item: SwotItem; focus?: 'quadrant' | 'priority' }
  | { kind: 'archive'; item: SwotItem }
  | { kind: 'history'; item: SwotItem }
  | { kind: 'original'; item: SwotItem };

export function SwotBoard({ analysisId, initial, canManage, go }: { analysisId: string; initial: SwotData; canManage: boolean; go: (aba: string) => void }) {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<SwotData>(initial);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const narrow = useNarrow();
  const editable = canManage && data.editable;
  const opener = useRef<HTMLElement | null>(null);

  const reload = async () => {
    setBusy(true);
    try { setData(await marketApi.swot(analysisId, data.runId)); } catch (e) { toast((e as Error).message, 'error'); } finally { setBusy(false); }
  };
  const open = (d: Dialog) => { opener.current = document.activeElement as HTMLElement | null; setDialog(d); };
  const close = () => { setDialog(null); window.setTimeout(() => opener.current?.focus?.(), 0); };
  const run = async (fn: () => Promise<unknown>, ok: string, action?: { label: string; onClick: () => void }) => {
    try { await fn(); toast(ok, 'info', 'InteliMove:', action); await reload(); } catch (e) { toast(mensagem(e), 'error'); await reload(); }
  };
  const restore = (it: SwotItem, version: number) => run(() => marketApi.restoreSwotItem(analysisId, it.id, version), `“${it.title}” voltou para ${QUADRANT_LABEL[it.quadrant]}.`);
  const archive = async (it: SwotItem, reason: string) => {
    try {
      const x = await marketApi.archiveSwotItem(analysisId, it.id, it.version, reason || undefined);
      close();
      toast(`“${it.title}” foi arquivado.`, 'info', 'InteliMove:', { label: 'Desfazer', onClick: () => void restore(x, x.version) });
      await reload();
    } catch (e) { toast(mensagem(e), 'error'); close(); await reload(); }
  };
  const move = (it: SwotItem, q: SwotQuadrant) => run(() => marketApi.updateSwotItem(analysisId, it.id, it.version, { quadrant: q }), `“${it.title}” foi movido para ${QUADRANT_LABEL[q]}.`);
  const decide = (it: SwotItem, d: SwotReviewDecision) => run(() => marketApi.resolveSwotReview(analysisId, it.id, it.version, d), DECISION_DONE[d]);

  const w = data.company;
  return (
    <div className="ctx-body swot-board" aria-busy={busy}>
      <p className="card-sub">Perspectiva: <b>{w}</b>. Forças e fraquezas são fatores internos de {w}; oportunidades e ameaças são fatores externos. A análise propõe os itens; você pode adicionar, editar, mover, arquivar e restaurar — o achado e as fontes originais nunca são alterados. A fragilidade de um concorrente não é fraqueza de {w}: aparece como possível oportunidade competitiva, a validar.</p>
      {!data.editable && data.readOnlyReason && (
        <div className="card notice" role="status">
          <b>{data.historical ? `Execução histórica (#${data.runNo})` : 'Somente consulta'}</b> <span className="card-sub">{data.readOnlyReason}</span>
          {data.historical && data.latestRunId && <> <button type="button" className="link-btn" onClick={() => { const p = new URLSearchParams(params); p.set('execucao', data.latestRunId!); setParams(p, { replace: true }); }}>Abrir a execução mais recente</button></>}
        </div>
      )}
      {data.editable && !canManage && <div className="card notice" role="status"><b>Somente consulta.</b> <span className="card-sub">Seu perfil pode ver a SWOT, mas adicionar, editar, mover ou arquivar itens exige perfil de administrador.</span></div>}
      {data.reviewCount > 0 && (
        <div className="card notice swot-review-banner" role="status">
          <b>{data.reviewCount === 1 ? '1 possível atualização encontrada' : `${data.reviewCount} possíveis atualizações encontradas`}</b>
          <span className="card-sub"> A nova execução trouxe achado(s) parecido(s) com itens que você curou. Nada foi alterado sozinho: decida em cada item marcado.</span>
        </div>
      )}
      <div className="swot-grid">
        {SWOT_QUADRANTS.map((q) => (
          <Quadrant key={q.key} q={q} data={data} items={data[q.key]} archived={data.archived.filter((x) => x.quadrant === q.q)} narrow={narrow} editable={editable}
            analysisId={analysisId} go={go} open={open} move={move} decide={decide} restore={(it) => restore(it, it.version)} />
        ))}
      </div>
      {data.competitorStrengths > 0 && <p className="cell-sub">{data.competitorStrengths} força(s) de concorrentes estão nos perfis, na aba <button className="link-btn" onClick={() => go('concorrentes')}>Concorrentes</button>.</p>}
      <Legend />
      {dialog?.kind === 'create' && <ItemDialog analysisId={analysisId} runId={data.runId} quadrant={dialog.quadrant} onClose={close} onSaved={async (msg) => { close(); toast(msg); await reload(); }} onConflict={async () => { close(); await reload(); }} />}
      {dialog?.kind === 'edit' && <ItemDialog analysisId={analysisId} runId={data.runId} item={dialog.item} focus={dialog.focus} onClose={close} onSaved={async (msg) => { close(); toast(msg); await reload(); }} onConflict={async () => { close(); await reload(); }} />}
      {dialog?.kind === 'archive' && <ArchiveItemDialog item={dialog.item} onCancel={close} onConfirm={(reason) => archive(dialog.item, reason)} />}
      {dialog?.kind === 'history' && <HistoryDialog analysisId={analysisId} item={dialog.item} onClose={close} />}
      {dialog?.kind === 'original' && <OriginalDialog item={dialog.item} onClose={close} />}
    </div>
  );
}

const DECISION_DONE: Record<SwotReviewDecision, string> = {
  KEEP_CURATED: 'Mantida a sua versão; a sugestão foi arquivada.', REPLACE: 'A nova sugestão substituiu o item curado (que ficou em Arquivados).',
  KEEP_BOTH: 'Os dois itens foram mantidos.', ARCHIVE_SUGGESTION: 'A nova sugestão foi arquivada.',
};

function mensagem(e: unknown): string {
  if (e instanceof ApiError && e.code === 'version_conflict') return 'Este item foi alterado por outra pessoa. A SWOT foi recarregada com a versão atual.';
  return (e as Error).message;
}

interface QuadrantProps {
  q: (typeof SWOT_QUADRANTS)[number]; data: SwotData; items: SwotItem[]; archived: SwotItem[]; narrow: boolean; editable: boolean; analysisId: string;
  go: (aba: string) => void; open: (d: Dialog) => void; move: (it: SwotItem, q: SwotQuadrant) => void; decide: (it: SwotItem, d: SwotReviewDecision) => void; restore: (it: SwotItem) => void;
}
function Quadrant({ q, data, items, archived, narrow, editable, analysisId, go, open, move, decide, restore }: QuadrantProps) {
  const [wantArchived, setShowArchived] = useState(false);
  // Restaurado o último arquivado, o quadrante volta sozinho aos itens ativos (o botão de alternar some).
  const showArchived = wantArchived && archived.length > 0;
  const [all, setAll] = useState(false);
  const active = data.counts[q.key];
  const pending = items.filter((x) => x.status === 'REVIEW').length;
  const visible = narrow && !all ? items.slice(0, PREVIEW) : items;
  const headId = `sw-${q.key}`;
  const internal = q.q === 'STRENGTHS' || q.q === 'WEAKNESSES';
  return (
    <section className={`card swot-q swot-${q.key}`} aria-labelledby={headId}>
      <header className="swot-q-head">
        <div className="swot-q-title">
          <h2 className="card-title" id={headId}>{q.label} <span className="swot-count">· {plural(active)}</span>{pending > 0 && <> <span className="tag amber">{pending} a revisar</span></>}</h2>
          <p className="cell-sub">{q.sub}</p>
        </div>
        <div className="swot-q-actions">
          {editable && <button type="button" className="chip" onClick={() => open({ kind: 'create', quadrant: q.q })} aria-label={`Adicionar item em ${q.label}`}>+ Adicionar item</button>}
          {archived.length > 0 && <button type="button" className="chip" aria-pressed={showArchived} onClick={() => setShowArchived((v) => !v)} aria-label={`${showArchived ? 'Voltar aos itens ativos de' : 'Ver arquivados de'} ${q.label} (${archived.length})`}>{showArchived ? 'Ver ativos' : `Arquivados (${archived.length})`}</button>}
        </div>
      </header>
      {/* Região rolável com foco por teclado; sem overscroll-behavior: no início/fim, a página continua a rolar. */}
      <div className="swot-scroll" tabIndex={0} role="region" aria-label={showArchived ? `Itens arquivados de ${q.label}` : `Itens de ${q.label}`}>
        {showArchived ? (
          <ul className="swot-list">{archived.map((it) => <ArchivedItem key={it.id} it={it} editable={editable} restore={restore} open={open} />)}</ul>
        ) : items.length ? (
          <>
            <ul className="swot-list">{visible.map((it) => <Item key={it.id} it={it} editable={editable} analysisId={analysisId} go={go} open={open} move={move} decide={decide} />)}</ul>
            {narrow && items.length > PREVIEW && <button type="button" className="secondary swot-more" aria-expanded={all} onClick={() => setAll((v) => !v)}>{all ? 'Mostrar menos' : `Ver todos os ${items.length} itens`}</button>}
          </>
        ) : (
          <div className="swot-empty">
            <p>{internal ? `Nenhum fator interno de ${data.company} registrado nesta execução.` : 'Nenhum item registrado nesta execução.'}</p>
            {editable && <p className="cell-sub">Use “Adicionar item” para registrar um.</p>}
          </div>
        )}
      </div>
    </section>
  );
}

function Item({ it, editable, analysisId, go, open, move, decide }: { it: SwotItem; editable: boolean; analysisId: string; go: (aba: string) => void; open: (d: Dialog) => void; move: (it: SwotItem, q: SwotQuadrant) => void; decide: (it: SwotItem, d: SwotReviewDecision) => void }) {
  const [menu, setMenu] = useState(false);
  const menuId = useId();
  // Escolher uma ação fecha o menu (ao voltar, o item está no estado novo e o menu, recolhido).
  // O foco vai para "Ações" antes: é para lá que ele volta quando o diálogo fecha.
  const toggle = useRef<HTMLButtonElement>(null);
  const act = (fn: () => void) => () => { toggle.current?.focus(); setMenu(false); fn(); };
  const moveId = useId();
  const review = it.status === 'REVIEW';
  // Item do contexto: a origem ("Importado do contexto da empresa") já diz de onde veio — sem repetir na perspectiva.
  const meta = [it.declared ? null : it.perspectiveLabel, it.impact ? `impacto ${it.impact.toLowerCase()}` : null, it.confidence !== null ? `confiança ${it.confidence}%` : null, it.related.length ? `relacionado a ${it.related.join(', ')}` : null].filter(Boolean).join(' · ');
  return (
    <li className={`swot-item${review ? ' review' : ''}`}>
      {review && (
        <div className="swot-review" role="group" aria-label="Possível atualização encontrada">
          <b>Possível atualização encontrada</b>
          <span className="cell-sub">A nova execução trouxe este achado, parecido com {it.reviewOf ? <>“{it.reviewOf.title}”{it.reviewOf.status === 'ARCHIVED' ? ' (arquivado)' : ''}</> : 'um item curado'}. Escolha o que fazer:</span>
          {editable && (
            <div className="row-actions">
              <button type="button" className="chip" onClick={() => decide(it, 'KEEP_CURATED')}>Manter a versão curada</button>
              <button type="button" className="chip" onClick={() => decide(it, 'REPLACE')}>Substituir pela nova</button>
              <button type="button" className="chip" onClick={() => decide(it, 'KEEP_BOTH')}>Manter ambas</button>
              <button type="button" className="chip" onClick={() => decide(it, 'ARCHIVE_SUGGESTION')}>Arquivar a sugestão</button>
            </div>
          )}
        </div>
      )}
      <div className="swot-item-top">
        <b className="swot-item-title">{it.title}</b>
        <span className="swot-tags">
          {it.priorityLabel && <span className="tag petrol">Prioridade {it.priorityLabel.toLowerCase()}</span>}
          {!it.declared && <Nature n={it.nature} />}
        </span>
      </div>
      <p className="swot-origin">{it.originLabels.join(' · ')}{it.newInRun ? ' · Novo nesta execução' : ''}</p>
      {meta && <p className="cell-sub">{meta}</p>}
      {it.description && <p>{it.description}</p>}
      {it.recommendedAction && <p className="cell-sub"><b>Ação recomendada:</b> {it.recommendedAction}</p>}
      {it.actions.length > 0 && <p className="cell-sub">Recomendação da análise: {it.actions.map((x) => x.title).join('; ')} — <button type="button" className="link-btn" onClick={() => go('estrategia')}>ver</button></p>}
      {it.observation && <p className="cell-sub"><b>Observação:</b> {it.observation}</p>}
      {it.note && <p className="cell-sub"><i>{it.note}</i></p>}
      {it.edited && !it.unsourced && <p className="cell-sub swot-warn">Texto alterado manualmente: as fontes abaixo sustentam o enunciado original.</p>}
      <div className="swot-item-foot">
        {!it.declared && <Sources analysisId={analysisId} runId={it.evidenceRunId} ids={it.evidenceIds} emptyText={it.manual ? 'Sem fonte associada' : 'Sem fonte citada'} />}
        <button ref={toggle} type="button" className="link-btn" aria-expanded={menu} aria-controls={menuId} onClick={() => setMenu((m) => !m)} aria-label={`Ações para “${it.title}”`}>{menu ? 'Fechar ações' : 'Ações'}</button>
        {menu && (
          <div className="row-actions" id={menuId}>
            {editable && !review && <button type="button" className="chip" onClick={act(() => open({ kind: 'edit', item: it }))}>Editar</button>}
            {editable && !review && (
              <span className="chip chip-select">
                <label htmlFor={moveId}>Mover para</label>
                <select id={moveId} value="" onChange={(e) => { const v = e.target.value as SwotQuadrant; if (v) act(() => move(it, v))(); }}>
                  <option value="">—</option>
                  {SWOT_QUADRANTS.filter((x) => x.q !== it.quadrant).map((x) => <option key={x.q} value={x.q}>{x.label}</option>)}
                </select>
              </span>
            )}
            {editable && !review && <button type="button" className="chip" onClick={act(() => open({ kind: 'edit', item: it, focus: 'priority' }))}>Prioridade</button>}
            {editable && <button type="button" className="chip" onClick={act(() => open({ kind: 'archive', item: it }))}>Arquivar</button>}
            {it.original && <button type="button" className="chip" onClick={act(() => open({ kind: 'original', item: it }))}>Ver original</button>}
            {!it.id.startsWith('00000000-0000-4000-8000') && <button type="button" className="chip" onClick={act(() => open({ kind: 'history', item: it }))}>Histórico</button>}
          </div>
        )}
      </div>
    </li>
  );
}

function ArchivedItem({ it, editable, restore, open }: { it: SwotItem; editable: boolean; restore: (it: SwotItem) => void; open: (d: Dialog) => void }) {
  return (
    <li className="swot-item archived">
      <b className="swot-item-title">{it.title}</b>
      <p className="swot-origin">{it.originLabels.join(' · ')}</p>
      <p className="cell-sub">Arquivado {it.archivedAt ? `em ${fmtDate(it.archivedAt)}` : ''}{it.archivedBy ? ` por ${it.archivedBy}` : ''}{it.archiveReason ? ` · ${it.archiveReason}` : ''}</p>
      <div className="row-actions">
        {editable && <button type="button" className="chip" onClick={() => restore(it)} aria-label={`Restaurar “${it.title}”`}>Restaurar</button>}
        <button type="button" className="chip" onClick={() => open({ kind: 'history', item: it })} aria-label={`Histórico de “${it.title}”`}>Histórico</button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Diálogos
// ---------------------------------------------------------------------------
type FormState = { quadrant: SwotQuadrant; title: string; description: string; recommendedAction: string; impact: string; priority: string; observation: string };
const FIELD_OF: Record<string, keyof FormState> = { quadrant: 'quadrant', title: 'title', description: 'description', recommendedAction: 'recommendedAction', impact: 'impact', priority: 'priority', observation: 'observation' };

export function ItemDialog({ analysisId, runId, item, quadrant, focus, onClose, onSaved, onConflict }: {
  analysisId: string; runId: string; item?: SwotItem; quadrant?: SwotQuadrant; focus?: 'quadrant' | 'priority';
  onClose: () => void; onSaved: (msg: string) => void; onConflict: () => void;
}) {
  const ini: FormState = {
    quadrant: item?.quadrant ?? quadrant ?? 'STRENGTHS', title: item?.title ?? '', description: item?.description ?? '', recommendedAction: item?.recommendedAction ?? '',
    impact: item?.impactLevel ?? '', priority: item?.priority ? String(item.priority) : '', observation: item?.observation ?? '',
  };
  const [v, setV] = useState<FormState>(ini);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [failure, setFailure] = useState<{ message: string; conflict: boolean } | null>(null);
  const [saving, setSaving] = useState(false);
  const ids = { form: useId(), title: useId(), description: useId(), action: useId(), impact: useId(), priority: useId(), quadrant: useId(), observation: useId() };
  const set = (k: keyof FormState) => (e: { target: { value: string } }) => { setV((x) => ({ ...x, [k]: e.target.value })); setErrors((x) => ({ ...x, [k]: undefined })); };
  const err = (k: keyof FormState, id: string) => (errors[k] ? { 'aria-invalid': true, 'aria-describedby': `${id}-err` } : {});
  const Err = ({ k, id }: { k: keyof FormState; id: string }) => (errors[k] ? <p className="field-error" id={`${id}-err`}>{errors[k]}</p> : null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const local: typeof errors = {};
    if (v.title.trim().length < 2) local.title = 'Informe um título com pelo menos 2 caracteres.';
    if (v.title.trim().length > 200) local.title = 'Título: no máximo 200 caracteres.';
    if (Object.keys(local).length) { setErrors(local); document.getElementById(ids.title)?.focus(); return; }
    const body: SwotFields = {
      quadrant: v.quadrant, title: v.title.trim(), description: v.description.trim() || null, recommendedAction: v.recommendedAction.trim() || null,
      impact: (v.impact || null) as SwotFields['impact'], priority: v.priority ? Number(v.priority) : null, observation: v.observation.trim() || null,
    };
    setSaving(true);
    setFailure(null);
    try {
      if (item) {
        const changed = Object.fromEntries(Object.entries(body).filter(([k, x]) => x !== ({ quadrant: item.quadrant, title: item.title, description: item.description || null, recommendedAction: item.recommendedAction, impact: item.impactLevel, priority: item.priority, observation: item.observation } as Record<string, unknown>)[k]));
        if (!Object.keys(changed).length) { onClose(); return; }
        await marketApi.updateSwotItem(analysisId, item.id, item.version, changed);
        onSaved('Item salvo.');
      } else {
        await marketApi.createSwotItem(analysisId, { ...body, quadrant: v.quadrant, title: v.title.trim(), runId });
        onSaved(`Item adicionado em ${QUADRANT_LABEL[v.quadrant]}.`);
      }
    } catch (x) {
      setSaving(false);
      if (x instanceof ApiError && Array.isArray(x.details)) {
        const fe: typeof errors = {};
        for (const d of x.details as Array<{ path: string; message: string }>) { const k = FIELD_OF[d.path]; if (k) fe[k] = d.message; }
        if (Object.keys(fe).length) { setErrors(fe); return; }
      }
      if (x instanceof ApiError && x.code === 'duplicate_item') { setErrors({ title: x.message }); return; }
      setFailure({ message: (x as Error).message, conflict: x instanceof ApiError && x.code === 'version_conflict' });
    }
  };
  const edited = !!item && item.origin !== 'USER' && (v.title.trim() !== item.title || v.description.trim() !== item.description);
  return (
    <Modal title={item ? 'Editar item da SWOT' : `Adicionar item em ${QUADRANT_LABEL[v.quadrant]}`} onClose={onClose} wide>
      <form id={ids.form} className="swot-form" onSubmit={submit} noValidate>
        {item && item.origin !== 'USER' && <p className="card-sub">O achado e as fontes originais da análise não mudam: a versão original continua disponível em “Ver original”, e o item passa a mostrar “Editado pelo usuário”.</p>}
        {!item && <p className="card-sub">O item ficará marcado como “Adicionado pelo usuário” e “Sem fonte associada” — não é tratado como fato comprovado.</p>}
        <div className="field wide"><label htmlFor={ids.title}>Título *</label><input id={ids.title} value={v.title} onChange={set('title')} maxLength={200} required autoComplete="off" data-autofocus={focus ? undefined : true} {...err('title', ids.title)} /><Err k="title" id={ids.title} /></div>
        <div className="field wide"><label htmlFor={ids.description}>Descrição</label><textarea id={ids.description} value={v.description} onChange={set('description')} maxLength={4000} {...err('description', ids.description)} /><Err k="description" id={ids.description} /></div>
        <div className="field wide"><label htmlFor={ids.action}>Ação recomendada</label><textarea id={ids.action} value={v.recommendedAction} onChange={set('recommendedAction')} maxLength={1000} rows={2} {...err('recommendedAction', ids.action)} /><Err k="recommendedAction" id={ids.action} /></div>
        <div className="swot-form-row">
          <div className="field"><label htmlFor={ids.quadrant}>Quadrante</label><select id={ids.quadrant} value={v.quadrant} onChange={set('quadrant')} data-autofocus={focus === 'quadrant' ? true : undefined} {...err('quadrant', ids.quadrant)}>{SWOT_QUADRANTS.map((x) => <option key={x.q} value={x.q}>{x.label}</option>)}</select><Err k="quadrant" id={ids.quadrant} /></div>
          <div className="field"><label htmlFor={ids.impact}>Impacto</label><select id={ids.impact} value={v.impact} onChange={set('impact')} {...err('impact', ids.impact)}>{IMPACT_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select><Err k="impact" id={ids.impact} /></div>
          <div className="field"><label htmlFor={ids.priority}>Prioridade</label><select id={ids.priority} value={v.priority} onChange={set('priority')} data-autofocus={focus === 'priority' ? true : undefined} {...err('priority', ids.priority)}>{PRIORITY_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select><Err k="priority" id={ids.priority} /></div>
        </div>
        <div className="field wide"><label htmlFor={ids.observation}>Observação</label><textarea id={ids.observation} value={v.observation} onChange={set('observation')} maxLength={1000} rows={2} {...err('observation', ids.observation)} /><Err k="observation" id={ids.observation} /></div>
        {edited && <p className="cell-sub swot-warn" role="note">Você está alterando o texto de um item da análise. A evidência original continua disponível, mas a matriz deixará claro que o texto foi alterado manualmente.</p>}
        {failure && (
          <div className="card notice danger" role="alert">
            <b>{failure.conflict ? 'Conflito de versão.' : 'Não foi possível salvar.'}</b> {failure.message}
            {failure.conflict && <> <button type="button" className="link-btn" onClick={onConflict}>Recarregar a SWOT</button></>}
          </div>
        )}
        <div className="form-actions">
          <button type="button" className="secondary" onClick={onClose}>Cancelar</button>
          <button type="submit" className="cta" disabled={saving}>{saving ? 'Salvando…' : item ? 'Salvar alterações' : 'Adicionar item'}</button>
        </div>
      </form>
    </Modal>
  );
}

function ArchiveItemDialog({ item, onCancel, onConfirm }: { item: SwotItem; onCancel: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const rid = useId();
  return (
    <Modal title={`Arquivar “${item.title}”?`} onClose={onCancel} role="alertdialog">
      <p className="card-sub">O item sai da matriz, mas nada é excluído: as fontes e o histórico continuam disponíveis em “Arquivados”, e você pode restaurá-lo. Uma nova execução da análise não o trará de volta sozinha.</p>
      <div className="field"><label htmlFor={rid}>Motivo (opcional)</label><input id={rid} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} autoComplete="off" /></div>
      <div className="form-actions">
        <button type="button" className="secondary" onClick={onCancel} data-autofocus>Cancelar</button>
        <button type="button" className="cta" disabled={busy} onClick={() => { setBusy(true); onConfirm(reason.trim()); }}>{busy ? 'Arquivando…' : 'Arquivar item'}</button>
      </div>
    </Modal>
  );
}

function OriginalDialog({ item, onClose }: { item: SwotItem; onClose: () => void }) {
  const o = item.original!;
  return (
    <Modal title="Conteúdo original" onClose={onClose} wide>
      <p className="card-sub">{item.origin === 'USER' ? 'Como o item foi criado.' : item.origin === 'CONTEXT' ? 'Como o diferencial foi declarado no contexto da empresa.' : 'Como a análise registrou o achado — as fontes do item sustentam este enunciado.'}</p>
      <dl className="swot-original">
        <dt>Quadrante</dt><dd>{o.quadrantLabel}{item.moved ? ` (hoje em ${QUADRANT_LABEL[item.quadrant]})` : ''}</dd>
        <dt>Título</dt><dd>{o.title}</dd>
        {o.description && <><dt>Descrição</dt><dd>{o.description}</dd></>}
        {o.impact && <><dt>Impacto</dt><dd>{o.impact}</dd></>}
      </dl>
      {item.edited && <p className="cell-sub">Editado {item.editedBy ? `por ${item.editedBy} ` : ''}{item.editedAt ? `em ${fmtDate(item.editedAt)}` : ''}.</p>}
      <div className="form-actions"><button type="button" className="secondary" onClick={onClose} data-autofocus>Fechar</button></div>
    </Modal>
  );
}

function HistoryDialog({ analysisId, item, onClose }: { analysisId: string; item: SwotItem; onClose: () => void }) {
  const [h, setH] = useState<SwotHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { marketApi.swotHistory(analysisId, item.id).then(setH, (e: Error) => setError(e.message)); }, [analysisId, item.id]);
  return (
    <Modal title={`Histórico de “${item.title}”`} onClose={onClose} wide>
      {error ? <p role="alert">{error}</p> : !h ? <p aria-busy="true">Carregando…</p> : (
        <ol className="swot-history">
          {h.events.map((e, i) => (
            <li key={i}>
              <b>{e.label}</b> <span className="cell-sub">{fmtDate(e.at)}{e.actor ? ` · ${e.actor}` : ''}{e.runNo ? ` · execução #${e.runNo}` : ''}</span>
              {e.detail && <p className="cell-sub">{e.detail}</p>}
              {e.changes.length > 0 && <ul className="plain-list">{e.changes.map((c) => <li key={c.field}><b>{c.label}:</b> {c.before ?? '—'} → {c.after ?? '—'}</li>)}</ul>}
            </li>
          ))}
        </ol>
      )}
      <div className="form-actions"><button type="button" className="secondary" onClick={onClose} data-autofocus>Fechar</button></div>
    </Modal>
  );
}

